// src/components/OAuthCallback.js
// Dedicated page for handling OAuth redirects - built from ground up for reliability
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getRedirectResult, onAuthStateChanged } from 'firebase/auth';
import { auth } from '../firebase';
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';
import { encrypt } from '../utils/encryption';
import { sendWelcomeEmail } from '../utils/email';
import { Loader2 } from 'lucide-react';
import Logo from './Logo';

// Backend logging function
const logToBackend = async (level, message, data = {}) => {
  try {
    const logData = {
      level,
      message,
      data: {
        ...data,
        url: window.location.href,
        userAgent: navigator.userAgent,
        timestamp: new Date().toISOString()
      },
      timestamp: new Date().toISOString()
    };
    
    // Always log to console first (this will always work)
    console.log(`[OAuthCallback ${level.toUpperCase()}]`, message, data);
    
    // Try to send to backend
    try {
      const response = await fetch('/api/log-oauth', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(logData)
      });
      
      if (!response.ok) {
        console.warn('[OAuthCallback] Backend log failed:', response.status, response.statusText);
      }
    } catch (fetchErr) {
      // Log the error but don't break the flow
      console.warn('[OAuthCallback] Failed to send log to backend:', fetchErr.message);
      console.warn('[OAuthCallback] Log data that failed to send:', logData);
    }
  } catch (err) {
    // Even if everything fails, at least log to console
    console.error('[OAuthCallback] Logging error:', err);
    console.log(`[OAuthCallback ${level.toUpperCase()}]`, message, data);
  }
};

function OAuthCallback() {
  const navigate = useNavigate();
  const [status, setStatus] = useState('Processing authentication...');
  const [error, setError] = useState(null);

  useEffect(() => {
    // Log immediately when component mounts - this helps verify the component is loading
    console.log('[OAuthCallback] Component mounted at', new Date().toISOString());
    console.log('[OAuthCallback] Current URL:', window.location.href);
    
    // Test logging immediately
    logToBackend('info', 'OAuthCallback component mounted', {
      timestamp: new Date().toISOString(),
      url: window.location.href
    }).catch(err => {
      console.error('[OAuthCallback] Initial log failed:', err);
    });

    let isMounted = true;
    let unsubscribe = null;
    let checkInterval = null;
    let maxAttempts = 0;

    const detectMobile = () => {
      return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) ||
             (window.innerWidth <= 768 && window.innerHeight <= 1024);
    };

    const processOAuthRedirect = async () => {
      try {
        const isMobile = detectMobile();
        const urlParams = new URLSearchParams(window.location.search);
        const hasGoogleParams = urlParams.has('code') || urlParams.has('state') || urlParams.has('error');
        
        await logToBackend('info', 'OAuth callback page loaded', {
          url: window.location.href,
          pathname: window.location.pathname,
          search: window.location.search,
          hash: window.location.hash,
          isMobile,
          hasOAuthIntent: !!sessionStorage.getItem('oauth_intent'),
          hasGoogleParams,
          urlParams: Object.fromEntries(urlParams.entries()),
          currentUser: auth.currentUser?.uid || null,
          referrer: document.referrer || 'none'
        });

        // Check if we're returning from Google (has OAuth params but no intent)
        if (hasGoogleParams && !sessionStorage.getItem('oauth_intent')) {
          await logToBackend('info', 'Detected return from Google OAuth', {
            hasCode: urlParams.has('code'),
            hasState: urlParams.has('state'),
            hasError: urlParams.has('error'),
            error: urlParams.get('error'),
            errorDescription: urlParams.get('error_description')
          });
        }

        // Check if we need to initiate OAuth (user came from Auth page)
        // NOTE: We no longer use redirect - popup is used for both mobile and desktop
        // If user somehow lands here, redirect them back to auth page
        const oauthIntent = sessionStorage.getItem('oauth_intent');
        if (oauthIntent === 'google') {
          await logToBackend('warn', 'OAuthCallback accessed but redirect flow is disabled - redirecting to auth', {
            currentUrl: window.location.href
          });
          sessionStorage.removeItem('oauth_intent');
          // Redirect back to auth page - popup should be used instead
          navigate('/auth', { replace: true });
          return;
        }

        // AGGRESSIVE CHECKING STRATEGY FOR MOBILE
        // On mobile, auth state can take time to propagate after redirect
        const checkDelay = isMobile ? 300 : 100;
        const maxWaitTime = isMobile ? 10000 : 5000; // 10 seconds on mobile
        const startTime = Date.now();

        await logToBackend('info', 'Starting auth detection strategies', {
          isMobile,
          checkDelay,
          maxWaitTime,
          currentUserBeforeCheck: auth.currentUser?.uid || null
        });

        // Strategy 1: Immediate check after short delay
        await new Promise(resolve => setTimeout(resolve, checkDelay));
        
        if (auth.currentUser && isMounted) {
          await logToBackend('success', 'Strategy 1: Found user immediately', {
            userId: auth.currentUser.uid,
            email: auth.currentUser.email
          });
          await handleAuthenticatedUser(auth.currentUser);
          return;
        }

        // Strategy 2: Try getRedirectResult
        setStatus('Checking authentication result...');
        try {
          await logToBackend('info', 'Strategy 2: Calling getRedirectResult');
          const result = await getRedirectResult(auth);
          if (result?.user && isMounted) {
            await logToBackend('success', 'Strategy 2: Got redirect result', {
              userId: result.user.uid,
              email: result.user.email,
              operationType: result.operationType
            });
            await handleAuthenticatedUser(result.user);
            return;
          } else {
            await logToBackend('info', 'Strategy 2: getRedirectResult returned null', {
              result: result ? 'result exists but no user' : 'result is null'
            });
          }
        } catch (redirectErr) {
          await logToBackend('warn', 'Strategy 2: getRedirectResult error', {
            errorCode: redirectErr.code,
            errorMessage: redirectErr.message
          });
        }

        // Strategy 3: Poll auth.currentUser aggressively (mobile needs this)
        await logToBackend('info', 'Strategy 3: Starting aggressive polling');
        setStatus('Waiting for authentication...');
        
        const pollForUser = () => {
          return new Promise((resolve) => {
            let attempts = 0;
            const maxPollAttempts = isMobile ? 50 : 30; // Check 50 times on mobile
            
            checkInterval = setInterval(() => {
              attempts++;
              maxAttempts = attempts;
              
              if (auth.currentUser) {
                logToBackend('success', `Strategy 3: Found user on attempt ${attempts}`, {
                  userId: auth.currentUser.uid,
                  email: auth.currentUser.email,
                  attempts,
                  elapsedTime: Date.now() - startTime
                });
                clearInterval(checkInterval);
                resolve(auth.currentUser);
                return;
              }
              
              // Log every 10th attempt
              if (attempts % 10 === 0) {
                logToBackend('info', `Strategy 3: Polling attempt ${attempts}`, {
                  attempts,
                  elapsedTime: Date.now() - startTime,
                  currentUser: auth.currentUser?.uid || null
                });
              }
              
              if (attempts >= maxPollAttempts || (Date.now() - startTime) > maxWaitTime) {
                logToBackend('warn', `Strategy 3: Polling stopped`, {
                  attempts,
                  maxAttempts,
                  elapsedTime: Date.now() - startTime,
                  maxWaitTime,
                  reason: attempts >= maxPollAttempts ? 'max attempts' : 'timeout'
                });
                clearInterval(checkInterval);
                resolve(null);
              }
            }, isMobile ? 200 : 150); // Check every 200ms on mobile
          });
        };

        // Strategy 4: Listen for auth state changes (fires immediately if user exists)
        await logToBackend('info', 'Strategy 4: Setting up onAuthStateChanged listener');
        const authStatePromise = new Promise((resolve) => {
          let resolved = false;
          unsubscribe = onAuthStateChanged(auth, (user) => {
            if (user && !resolved) {
              logToBackend('success', 'Strategy 4: Auth state changed', {
                userId: user.uid,
                email: user.email
              });
              resolved = true;
              resolve(user);
            } else if (!user && !resolved) {
              logToBackend('info', 'Strategy 4: Auth state changed to null');
            }
          });
          
          // Also check immediately in case user is already authenticated
          setTimeout(() => {
            if (!resolved && auth.currentUser) {
              logToBackend('success', 'Strategy 4: Found user on immediate check', {
                userId: auth.currentUser.uid,
                email: auth.currentUser.email
              });
              resolved = true;
              resolve(auth.currentUser);
            }
          }, 100);
        });

        // Race between polling and auth state listener
        const timeoutPromise = new Promise((resolve) => {
          setTimeout(() => {
            console.log('[OAuthCallback] Overall timeout reached');
            resolve(null);
          }, maxWaitTime);
        });

        const user = await Promise.race([
          pollForUser(),
          authStatePromise,
          timeoutPromise
        ]);

        // Clean up polling
        if (checkInterval) {
          clearInterval(checkInterval);
        }

        if (user && isMounted) {
          await logToBackend('success', 'User found via race condition', {
            userId: user.uid,
            email: user.email,
            strategy: 'race'
          });
          await handleAuthenticatedUser(user);
        } else if (isMounted) {
          // Final comprehensive check
          await logToBackend('info', 'Final check after all strategies', {
            maxAttempts,
            elapsedTime: Date.now() - startTime,
            currentUser: auth.currentUser?.uid || null
          });
          
          if (auth.currentUser) {
            await logToBackend('success', 'Final check found user', {
              userId: auth.currentUser.uid,
              email: auth.currentUser.email
            });
            await handleAuthenticatedUser(auth.currentUser);
          } else {
            await logToBackend('error', 'No user found after all strategies', {
              maxAttempts,
              elapsedTime: Date.now() - startTime,
              url: window.location.href,
              sessionStorage: {
                oauthIntent: sessionStorage.getItem('oauth_intent'),
                keys: Object.keys(sessionStorage)
              }
            });
            setError('Authentication failed. Please try again.');
            setTimeout(() => {
              if (isMounted) {
                navigate('/auth', { replace: true });
              }
            }, 3000);
          }
        }
      } catch (err) {
        await logToBackend('error', 'Exception in processOAuthRedirect', {
          errorMessage: err.message,
          errorCode: err.code,
          errorStack: err.stack,
          url: window.location.href
        });
        if (isMounted) {
          setError(err.message || 'Authentication error occurred');
          setTimeout(() => {
            if (isMounted) {
              navigate('/auth', { replace: true });
            }
          }, 3000);
        }
      }
    };

    const handleAuthenticatedUser = async (user) => {
      if (!isMounted || !user) {
        await logToBackend('warn', 'handleAuthenticatedUser called with invalid user', {
          isMounted,
          hasUser: !!user,
          userId: user?.uid
        });
        return;
      }

      try {
        await logToBackend('info', 'Starting user document setup', {
          userId: user.uid,
          email: user.email
        });
        setStatus('Setting up your account...');

        // Create user document if it doesn't exist
        const userDocRef = doc(db, 'users', user.uid);
        const userDoc = await getDoc(userDocRef);

        if (!userDoc.exists()) {
          const displayName = user.displayName || '';
          const nameParts = displayName.split(' ');
          const firstName = nameParts[0] || '';
          const lastName = nameParts.slice(1).join(' ') || '';

          // Encrypt sensitive data before saving (matches UserProfile.js pattern)
          const encryptionKey = process.env.REACT_APP_ENCRYPTION_KEY;
          await setDoc(userDocRef, {
            firstName: encrypt(firstName, encryptionKey),
            lastName: encrypt(lastName, encryptionKey),
            email: encrypt(user.email, encryptionKey),
            avatarUrl: user.photoURL || '',
            createdAt: serverTimestamp(),
          });
          await logToBackend('success', 'User document created', {
            userId: user.uid,
            firstName,
            lastName
          });

          // Send welcome email after OAuth account creation
          // Skip for Apple relay addresses (privacy feature) or if email is missing
          if (user.email && !user.email.includes('privaterelay.appleid.com')) {
            try {
              console.log('[OAuthCallback] Sending welcome email to:', user.email);
              await sendWelcomeEmail(user.email, firstName || 'there');
              console.log('[OAuthCallback] Welcome email sent successfully');
              await logToBackend('info', 'Welcome email sent successfully', {
                email: user.email
              });
            } catch (emailError) {
              // Don't fail signup if welcome email fails - log and continue
              console.error('[OAuthCallback] Failed to send welcome email:', {
                error: emailError.message,
                stack: emailError.stack,
                email: user.email
              });
              await logToBackend('warn', 'Failed to send welcome email', {
                error: emailError.message,
                stack: emailError.stack,
                email: user.email
              });
            }
          } else if (user.email && user.email.includes('privaterelay.appleid.com')) {
            console.log('[OAuthCallback] Skipping welcome email for Apple relay address:', user.email);
            await logToBackend('info', 'Skipping welcome email for Apple relay address', {
              email: user.email
            });
          } else {
            console.log('[OAuthCallback] No email available for welcome email');
          }
        } else {
          await logToBackend('info', 'User document already exists', {
            userId: user.uid
          });
        }

        // Verify user is still authenticated
        if (!auth.currentUser || auth.currentUser.uid !== user.uid) {
          await logToBackend('error', 'User auth state lost during processing', {
            originalUserId: user.uid,
            currentUserId: auth.currentUser?.uid || null
          });
          throw new Error('Authentication state lost');
        }

        setStatus('Redirecting to your dashboard...');
        await logToBackend('info', 'Preparing to navigate to home', {
          userId: user.uid
        });

        // Wait a moment for everything to settle
        await new Promise(resolve => setTimeout(resolve, 500));

        // Navigate to home
        if (isMounted && auth.currentUser) {
          await logToBackend('success', 'Navigating to home page', {
            userId: auth.currentUser.uid
          });
          navigate('/', { replace: true });
        } else {
          await logToBackend('warn', 'Navigation aborted - component unmounted or user lost', {
            isMounted,
            hasCurrentUser: !!auth.currentUser
          });
        }
      } catch (err) {
        await logToBackend('error', 'Error handling authenticated user', {
          errorMessage: err.message,
          errorCode: err.code,
          errorStack: err.stack,
          userId: user?.uid
        });
        if (isMounted) {
          setError(err.message || 'Error setting up account');
          setTimeout(() => {
            if (isMounted) {
              navigate('/auth', { replace: true });
            }
          }, 3000);
        }
      }
    };

    // Start processing
    processOAuthRedirect();

    return () => {
      isMounted = false;
      if (unsubscribe) {
        unsubscribe();
      }
      if (checkInterval) {
        clearInterval(checkInterval);
      }
    };
  }, [navigate]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 to-indigo-950 flex flex-col items-center justify-center p-8">
      <div className="text-center">
        <div className="mb-8">
          <Logo size="large" />
        </div>
        <div className="mb-6">
          <Loader2 className="w-12 h-12 animate-spin text-white mx-auto" />
        </div>
        <h1 className="text-2xl font-bold text-white mb-2">
          {error ? 'Authentication Error' : 'Completing Sign In'}
        </h1>
        <p className="text-slate-300 text-lg">
          {error || status}
        </p>
        {error && (
          <button
            onClick={() => navigate('/auth', { replace: true })}
            className="mt-6 px-6 py-2 bg-white text-slate-900 rounded-md font-medium hover:bg-slate-100 transition-colors"
          >
            Return to Login
          </button>
        )}
      </div>
    </div>
  );
}

export default OAuthCallback;

